import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import API_URL from "../components/config";
import axios from "axios";
import CategoriesPagination from "../components/blog/CategoriesPagination";
import Hero from "../components/blog/Hero";
import CTA from "../components/blog/Cta";
import Pagination from "../components/blog/Pagination";
import { Helmet } from "react-helmet";

function BlogSkeleton({ count = 6 }) {
  return (
    <div className="space-y-10 sm:space-y-12">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse grid grid-cols-1 md:grid-cols-2 gap-8 items-center"
        >
          <div className="aspect-[16/10] bg-gray-800/50 rounded-xl" />
          <div>
            <div className="flex gap-2 mb-4">
              <div className="h-4 w-20 bg-gray-800/60 rounded" />
              <div className="h-4 w-16 bg-gray-800/40 rounded" />
            </div>
            <div className="h-8 w-3/4 bg-gray-700/80 rounded mb-4" />
            <div className="h-4 w-full bg-gray-800/60 rounded mb-2" />
            <div className="h-4 w-5/6 bg-gray-800/60 rounded mb-6" />
            <div className="flex items-center justify-between">
              <div className="h-3 w-24 bg-gray-800/40 rounded" />
              <div className="flex gap-3">
                <div className="h-4 w-4 bg-gray-800/40 rounded-full" />
                <div className="h-4 w-4 bg-gray-800/40 rounded-full" />
                <div className="h-4 w-4 bg-gray-800/40 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BlogPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoriesPage, setCategoriesPage] = useState(1);

  const postsPerPage = 6;
  const categoriesPerPage = 7;

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/blogs`)
      .then((response) => {
        const sortedBlogs = (response.data || []).sort(
          (a, b) =>
            new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
        );
        setBlogPosts(sortedBlogs);
        setFilteredPosts(sortedBlogs);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load blog posts.");
        setLoading(false);
      });
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // All unique categories
  const allCategories = [
    ...new Set(
      blogPosts.flatMap((post) => {
        if (Array.isArray(post.categories)) {
          return post.categories;
        } else if (post.categories) {
          return [post.categories];
        }
        return [];
      })
    ),
  ];

  // Paginate categories
  const indexOfLastCategory = categoriesPage * categoriesPerPage;
  const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
  const currentCategories = allCategories.slice(
    indexOfFirstCategory,
    indexOfLastCategory
  );
  const totalCategoriesPages = Math.ceil(
    allCategories.length / categoriesPerPage
  );

  // Filter posts by category
  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
      setFilteredPosts(blogPosts);
    } else {
      setSelectedCategory(category);
      const filtered = blogPosts.filter((post) => {
        if (Array.isArray(post.categories)) {
          return post.categories.includes(category);
        } else if (post.categories) {
          return post.categories === category;
        }
        return false;
      });
      setFilteredPosts(filtered);
    }
    setCurrentPage(1);
  };

  // Featured & recent posts
  const featuredPosts = blogPosts
    .filter((post) => post.featured === true)
    .slice(0, 3);
  const recentPosts = blogPosts.slice(0, 5);

  // Share URLs – keep title-based slug for now
  const shareUrl = (platform, title) => {
    const url = encodeURIComponent(
      `https://www.frostandgrey.com/blog/${title
        .replace(/\s+/g, "-")
        .toLowerCase()}`
    );
    const text = encodeURIComponent(title);
    switch (platform) {
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
      case "x":
        return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
      case "linkedin":
        return `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`;
      default:
        return "#";
    }
  };

  // Pagination for posts
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage) || 1;

  function stripHTML(html) {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html || "";
    return tmp.textContent || tmp.innerText || "";
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen">
        <Hero />
        <div className="max-w-7xl mx-auto py-16 sm:py-20 lg:py-24 px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            <aside className="lg:col-span-1 order-2 lg:order-1" />
            <main
              className="lg:col-span-2 order-1 lg:order-2"
              aria-busy="true"
            >
              <BlogSkeleton count={6} />
            </main>
            <aside className="lg:col-span-1 order-3" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-black text-white min-h-screen">
        <Hero />
        <div className="flex justify-center items-center py-24 px-6">
          <div className="text-center max-w-md">
            <p className="text-red-400 tracking-[0.2em] uppercase text-xs sm:text-sm">
              Content Unavailable
            </p>
            <p className="text-gray-500 text-xs sm:text-sm mt-3 leading-relaxed">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <Helmet>
        <title>AI Insights, Governance & Industry Analysis | Frost & Grey Blog</title>

        <meta
          name="description"
          content="Read Frost & Grey’s latest insights on AI training, enterprise AI strategy, automation, AI governance, and digital transformation across Africa."
        />

        <link rel="canonical" href="https://frostandgrey.com/blog" />

        <meta property="og:title" content="Frost & Grey Blog — AI Insights" />
        <meta
          property="og:description"
          content="Exploring AI adoption, ethics, training, and transformation across Africa."
        />
        <meta property="og:url" content="https://frostandgrey.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Hero />

      <div className="max-w-7xl mx-auto py-16 sm:py-20 lg:py-24 px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Left Sidebar - Featured */}
          <aside className="lg:col-span-1 order-2 lg:order-1">
            <div className="sticky top-8 space-y-10">
              {/* Featured */}
              <div>
                <h3 className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6 border-b border-gray-800 pb-2">
                  Featured
                </h3>
                {featuredPosts.length > 0 ? (
                  <div className="space-y-8">
                    {featuredPosts.slice(0, 2).map((post) => (
                      <Link key={post.slug} to={`/blog/${post.slug}`}>
                        <article className="group cursor-pointer">
                          <div className="aspect-[4/3] overflow-hidden mb-3 rounded-lg">
                            {post.image && (
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              />
                            )}
                          </div>
                          <h4 className="text-sm font-light leading-tight text-gray-300 group-hover:text-white transition-colors duration-300">
                            {post.title.length > 60
                              ? `${post.title.slice(0, 60)}…`
                              : post.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-2 tracking-wide">
                            {new Date(
                              post.createdAt || post.date
                            ).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </article>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs tracking-wide">
                    No featured content available
                  </p>
                )}
              </div>

              {/* Social Links */}
              <div className="border-t border-gray-800 pt-8">
                <h3 className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6">
                  Connect
                </h3>
                <div className="flex space-x-4">
                  <a
                    href="https://web.facebook.com/profile.php?id=100092843855928"
                    target="_blank"
                    rel="noreferrer"
                    className="text-gray-500 hover:text-white transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/_frostandgrey/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-gray-500 hover:text-white transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/frost-grey"
                    target="_blank"
                    rel="noreferrer"
                    className="text-gray-500 hover:text-white transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-2 order-1 lg:order-2">
            {/* Category Filter Badge */}
            {selectedCategory && (
              <div className="mb-8 flex flex-wrap items-center gap-3">
                <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-gray-400">
                  Filtered by:
                </span>
                <span className="px-3 py-1 bg-gray-800 text-gray-200 text-xs tracking-wide rounded-sm">
                  {selectedCategory}
                </span>
                <button
                  onClick={() => handleCategoryClick(selectedCategory)}
                  className="text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Articles */}
            {currentPosts.length > 0 ? (
              <div className="space-y-10 sm:space-y-12">
                {currentPosts.map((post, index) => {
                  const previewText = stripHTML(post.content || "");
                  const shortPreview =
                    previewText.length > 150
                      ? `${previewText.slice(0, 150)}…`
                      : previewText || "Content preview unavailable";

                  const imageOrder =
                    index % 2 === 0 ? "md:order-1" : "md:order-2";
                  const textOrder =
                    index % 2 === 0 ? "md:order-2" : "md:order-1";

                  return (
                    <article key={post.slug} className="group">
                      <Link to={`/blog/${post.slug}`}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                          <div
                            className={`aspect-[16/10] overflow-hidden rounded-lg ${imageOrder}`}
                          >
                            {post.image && (
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              />
                            )}
                          </div>
                          <div className={textOrder}>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {Array.isArray(post.categories) &&
                                post.categories.map((category) => (
                                  <span
                                    key={category}
                                    className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-gray-500 border-b border-gray-700 pb-1"
                                  >
                                    {category}
                                  </span>
                                ))}
                            </div>
                            <h2 className="text-xl sm:text-2xl font-thin leading-tight mb-3 sm:mb-4 text-gray-100 group-hover:text-white transition-colors duration-300">
                              {post.title}
                            </h2>
                            <p className="text-gray-400 leading-relaxed mb-5 sm:mb-6 font-light text-sm sm:text-sm">
                              {shortPreview}
                            </p>
                            <div className="flex items-center justify-between">
                              <time className="text-[10px] sm:text-xs tracking-[0.2em] text-gray-500">
                                {new Date(
                                  post.createdAt || post.date
                                ).toLocaleDateString("en-US", {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </time>
                              <div className="flex gap-3">
                                <a
                                  href={shareUrl("facebook", post.title)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-600 hover:text-gray-300 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                                  </svg>
                                </a>
                                <a
                                  href={shareUrl("x", post.title)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-600 hover:text-gray-300 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 300 300"
                                  >
                                    <path d="M178.57 127.15 290.27 0h-26.46l-97.03 110.38L89.34 0H0l117.13 166.93L0 300.25h26.46l102.4-116.59 81.8 116.59h89.34M36.01 19.54H76.66l187.13 262.13h-40.66" />
                                  </svg>
                                </a>
                                <a
                                  href={shareUrl("linkedin", post.title)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-600 hover:text-gray-300 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                  </svg>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20 sm:py-24">
                <div className="w-px h-10 sm:h-12 bg-gradient-to-b from-transparent via-gray-400 to-transparent mx-auto mb-6" />
                <p className="text-gray-400 text-base sm:text-lg font-light tracking-wide">
                  No articles available
                </p>
                {selectedCategory && (
                  <p className="text-gray-600 text-xs sm:text-sm mt-2 tracking-wide">
                    No content found for “{selectedCategory}”.
                  </p>
                )}
              </div>
            )}
          </main>

          {/* Right Sidebar */}
          <aside className="lg:col-span-1 order-3">
            <div className="sticky top-8 space-y-12">
              {/* Categories */}
              <div>
                <h3 className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6 border-b border-gray-800 pb-2">
                  Categories
                </h3>
                {currentCategories.length > 0 ? (
                  <>
                    <ul className="space-y-3">
                      {currentCategories.map((category) => (
                        <li key={category}>
                          <button
                            onClick={() => handleCategoryClick(category)}
                            className={`w-full text-left text-sm font-light tracking-wide transition-colors duration-300 ${
                              selectedCategory === category
                                ? "text-white border-b border-white pb-1"
                                : "text-gray-400 hover:text-gray-200"
                            }`}
                          >
                            {category}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <CategoriesPagination
                      categoriesPage={categoriesPage}
                      totalCategoriesPages={totalCategoriesPages}
                      setCategoriesPage={setCategoriesPage}
                    />
                  </>
                ) : (
                  <p className="text-gray-500 text-xs tracking-wide">
                    No categories available
                  </p>
                )}
              </div>

              {/* Recent Posts */}
              <div>
                <h3 className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6 border-b border-gray-800 pb-2">
                  Recent
                </h3>
                {recentPosts.length > 0 ? (
                  <div className="space-y-6">
                    {recentPosts.map((post) => (
                      <Link key={post.slug} to={`/blog/${post.slug}`}>
                        <article className="group cursor-pointer">
                          <h4 className="text-sm font-light leading-tight text-gray-300 group-hover:text-white transition-colors duration-300 mb-2">
                            {post.title.length > 50
                              ? `${post.title.slice(0, 50)}…`
                              : post.title}
                          </h4>
                          <p className="text-xs text-gray-500 tracking-wide">
                            {new Date(
                              post.createdAt || post.date
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </article>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs tracking-wide">
                    No recent posts available
                  </p>
                )}
              </div>

              {/* Newsletter Signup */}
              <div className="border-t border-gray-800 pt-8">
                <h3 className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6">
                  Subscribe
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-6 font-light">
                  Stay updated with our latest AI insights, governance
                  perspectives, and industry intelligence.
                </p>
                <div className="space-y-4">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="w-full bg-transparent border border-gray-700 focus:border-gray-500 px-4 py-3 text-sm text-gray-300 placeholder-gray-600 focus:outline-none transition-colors"
                  />
                  <button className="w-full border border-gray-700 hover:border-gray-500 hover:bg-gray-900/50 px-4 py-3 text-xs tracking-[0.1em] uppercase font-light text-gray-300 hover:text-white transition-all duration-300">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-16">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
